import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import {
  Avatar,
  Breadcrumb,
  Button,
  Pagination,
  Table,
  Spinner,
  Tooltip,
} from "flowbite-react";
import { HiAdjustments, HiX } from "react-icons/hi";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { HiViewColumns } from "react-icons/hi2";

import { HiChevronUp, HiChevronDown } from "react-icons/hi";

import {
  PaginationInfo,
  RenewalForecastPill,
  TextInput,
  TooltipContent,
  UnassignedBadge,
  ScrollableTableWrapper,
} from "../../components";
import { SelectedFilters } from "./SelectedFilters";
import { COLUMN_HEADER_TOOLTIP } from "./CustomerListPage.utils";
import { useEmptyStateTracking } from "../../hooks/useEmptyStateTracking";
import { getFormattedMoney } from "../../utils";

/**
 * Maps column IDs to backend ordering field names.
 * Columns not in this map are not sortable.
 */
const COLUMN_ORDERING_MAP = {
  account: "name",
  mrr: "mrr",
  arr: "mrr", // ARR is 12x MRR, so sorting by MRR achieves the same order
  daysTillRenewal: "days_till_renewal",
  healthScore: "health_score",
  nps: "nps",
  csat: "csat",
  seatUtilization: "seat_utilization",
  numEmployees: "num_employees",
  churnDate: "churn_date",
};

/**
 * Renders a single table cell for a given column ID and customer row.
 * Ensures cells render in the same order as column headers.
 * Accesses customer fields directly to avoid unnecessary destructuring per cell.
 */
const renderCell = (columnId, c, formatDaysTillRenewal) => {
  switch (columnId) {
    case "account":
      return (
        <Table.Cell
          key={columnId}
          className="sticky left-0 z-10 bg-inherit shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]"
        >
          <div className="flex items-center space-x-3 font-bold hover:text-blue-500">
            <Avatar size="xs" img={c.logo} className="flex justify-start">
              <div className="flex items-center gap-2">
                <HighlightedText
                  text={c.name}
                  highlights={c.highlights?.name}
                  fallback={c.name}
                />
                {c.is_unassigned && <UnassignedBadge />}
                {c.subscription_status === "pilot" && (
                  <span className="rounded border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700">
                    Pilot
                  </span>
                )}
              </div>
            </Avatar>
          </div>
        </Table.Cell>
      );
    case "segment":
      return (
        <Table.Cell key={columnId}>
          <HighlightedText
            text={c.industry}
            highlights={c.highlights?.segment || c.highlights?.industry}
            fallback={c.industry}
          />
        </Table.Cell>
      );
    case "mrr":
      return (
        <Table.Cell key={columnId} className="font-bold">
          {getFormattedMoney(
            c.subscription_status === "pilot" ? c.pilot_mrr : c.mrr,
          )}
        </Table.Cell>
      );
    case "arr":
      return (
        <Table.Cell key={columnId} className="font-bold">
          {getFormattedMoney(
            c.subscription_status === "pilot" ? c.pilot_arr : c.arr,
          )}
        </Table.Cell>
      );
    case "deltaMrr":
      return (
        <Table.Cell key={columnId} className="font-bold">
          {getFormattedMoney(
            c.subscription_status === "pilot" ? c.pilot_delta_mrr : c.delta_mrr,
          )}
        </Table.Cell>
      );
    case "deltaArr":
      return (
        <Table.Cell key={columnId} className="font-bold">
          {getFormattedMoney(
            c.subscription_status === "pilot" ? c.pilot_delta_arr : c.delta_arr,
          )}
        </Table.Cell>
      );
    case "daysTillRenewal":
      return (
        <Table.Cell key={columnId}>
          {formatDaysTillRenewal(c.days_till_renewal)}
        </Table.Cell>
      );
    case "location":
      return (
        <Table.Cell key={columnId}>
          <HighlightedText
            text={c.location}
            highlights={c.highlights?.location}
            fallback={c.location}
          />
        </Table.Cell>
      );
    case "renewalForecast":
      return (
        <Table.Cell key={columnId}>
          <RenewalForecastPill renewalForecast={c.renewal_forecast} />
        </Table.Cell>
      );
    case "healthScore":
      return (
        <Table.Cell key={columnId}>
          <div className="flex items-center gap-1">
            <span>{c.health_score != null ? c.health_score : "-"}</span>
            {c.confidence_level && (
              <span
                className={`inline-block h-2 w-2 rounded-full ${
                  c.confidence_level === "high"
                    ? "bg-green-400"
                    : c.confidence_level === "medium"
                      ? "bg-yellow-400"
                      : "bg-red-400"
                }`}
                title={`Confidence: ${c.confidence_level}`}
              />
            )}
          </div>
        </Table.Cell>
      );
    case "nps":
      return (
        <Table.Cell key={columnId}>{c.nps != null ? c.nps : "-"}</Table.Cell>
      );
    case "csat":
      return (
        <Table.Cell key={columnId}>{c.csat != null ? c.csat : "-"}</Table.Cell>
      );
    case "seatUtilization":
      return (
        <Table.Cell key={columnId}>
          {c.seat_utilization != null ? `${c.seat_utilization}%` : "-"}
        </Table.Cell>
      );
    case "numEmployees":
      return (
        <Table.Cell key={columnId}>
          {c.num_employees != null ? c.num_employees.toLocaleString() : "-"}
        </Table.Cell>
      );
    case "csOwner":
      return <Table.Cell key={columnId}>{c.cs_owner_name || "-"}</Table.Cell>;
    case "lifecycleStage":
      return <Table.Cell key={columnId}>{c.lifecycle_stage || "-"}</Table.Cell>;
    case "churnDate":
      return (
        <Table.Cell key={columnId}>
          {c.churn_date
            ? new Date(c.churn_date).toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
              })
            : "-"}
        </Table.Cell>
      );
    default:
      return <Table.Cell key={columnId}>-</Table.Cell>;
  }
};

const HighlightedText = ({ text, highlights, fallback }) => {
  const content = highlights || fallback || text;

  if (highlights && highlights !== text && highlights.includes("<mark")) {
    return <span dangerouslySetInnerHTML={{ __html: content }} />;
  }

  return <span>{content}</span>;
};

const SkeletonRow = ({ numOfCells = 5 }) => (
  <Table.Row className="animate-pulse">
    <Table.Cell className="sticky left-0 z-10 bg-white shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
      <div className="flex items-center space-x-3">
        <div className="h-8 w-8 rounded-full bg-gray-200 dark:bg-gray-700"></div>
        <div className="h-4 w-32 rounded bg-gray-200 dark:bg-gray-700"></div>
      </div>
    </Table.Cell>
    {Array.from({ length: numOfCells }).map((_, index) => (
      <Table.Cell key={index}>
        <div className="h-4 w-20 rounded bg-gray-200 dark:bg-gray-700"></div>
      </Table.Cell>
    ))}
  </Table.Row>
);

export const CustomersTable = ({
  customerList,
  handleFiltersOpen,
  handleColumnsOpen,
  columns,
  pagination,
  fetchNextPage,
  isLoading,
  selectedFilters,
  handleFilterToggle,
  onSearch,
  searchTerm = "",
  isSearching = false,
  ordering = "",
  onOrderingChange,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);
  const [previousCustomerList, setPreviousCustomerList] = useState([]);
  const selectedColumnIds = columns
    .filter((col) => col.checked)
    .map((col) => col.id);

  // Use useRef to store the timeout ID to prevent memory leaks
  const timeoutRef = useRef(null);

  useEmptyStateTracking("customer_list", {
    isEmpty: customerList.length === 0,
    isLoading: isLoading || isSearching,
    properties: {
      has_active_filters: (selectedFilters?.length ?? 0) > 0,
      has_search_term: searchTerm.length > 0,
    },
  });

  // Sync local search term when parent search term changes (on back navigation)
  useEffect(() => {
    setLocalSearchTerm(searchTerm);
  }, [searchTerm]);

  // Keep track of previous results for smooth transitions
  const displayCustomerList = useMemo(() => {
    if (isSearching && previousCustomerList.length > 0) {
      // Show previous results while searching
      return previousCustomerList;
    }
    if (!isSearching && customerList.length > 0) {
      // Update previous results when not searching
      setPreviousCustomerList(customerList);
      return customerList;
    }
    return customerList;
  }, [customerList, isSearching, previousCustomerList]);

  // Fixed debounced search handler
  const handleSearch = useCallback(
    (value) => {
      setLocalSearchTerm(value);

      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set new timeout
      timeoutRef.current = setTimeout(() => {
        if (onSearch) {
          onSearch(value);
        }
      }, 500);
    },
    [onSearch],
  );

  const handleSearchInputChange = useCallback(
    (e) => {
      const value = e.target.value;
      handleSearch(value);
    },
    [handleSearch],
  );

  const handleSearchKeyPress = useCallback(
    (e) => {
      if (e.key === "Enter" && onSearch) {
        // Clear existing timeout and search immediately
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        onSearch(localSearchTerm);
      }
    },
    [localSearchTerm, onSearch],
  );

  const handleClearSearch = useCallback(() => {
    setLocalSearchTerm("");
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (onSearch) {
      onSearch("");
    }
  }, [onSearch]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const { page, totalPages, total, pageSize } = pagination;

  // Format the canonical `days_till_renewal` integer from the API.
  // Reading the integer (rather than recomputing from a renewal_date string)
  // keeps this cell in sync with the backend's future-only filter, so accounts
  // with no future contract end_date render as "-" instead of "Past Due".
  const formatDaysTillRenewal = (days) => {
    if (days === null || days === undefined) return "-";
    if (days < 0) return "Past Due";
    if (days === 0) return "Today";
    if (days === 1) return "Tomorrow";
    return `${days} days`;
  };

  // Only show full page loading on initial load
  if (isLoading && displayCustomerList.length === 0) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <Spinner aria-label="Loading customers" size="xl" />
      </div>
    );
  }

  return (
    <div className="animation-slide-up-lil-bounce flex h-screen w-full flex-col overflow-hidden bg-white dark:bg-gray-900">
      <Breadcrumb className="ml-5 mt-6">
        <Breadcrumb.Item>{t("pages.customers.title")}</Breadcrumb.Item>
      </Breadcrumb>

      <div className="my-6 ml-5 flex items-center justify-between">
        <h1 className="text-4xl font-bold text-black dark:text-white">
          {t("pages.customers.title")}
        </h1>
      </div>

      <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-white px-5 py-4 dark:bg-gray-900">
        <div className="relative flex w-1/3 items-center gap-2">
          <TextInput
            placeholder="Search customers (3+ characters)"
            value={localSearchTerm}
            onChange={handleSearchInputChange}
            onKeyPress={handleSearchKeyPress}
          />

          {localSearchTerm && (
            <Button
              onClick={handleClearSearch}
              aria-label="Clear search"
              size="xs"
              color="light"
            >
              {isSearching && <Spinner size="sm" className="h-6 w-6" />}
              {!isSearching && <HiX className="h-6 w-6" />}
            </Button>
          )}
        </div>

        <div className="flex items-center space-x-2.5">
          <Button color="gray" onClick={handleFiltersOpen}>
            <HiAdjustments className="mr-2 h-5 w-5" />
            {t("pages.customers.filter_one")}
          </Button>
          <Button color="gray" onClick={handleColumnsOpen}>
            <HiViewColumns className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {selectedFilters.length > 0 && (
        <SelectedFilters
          selectedFilters={selectedFilters}
          handleFilterToggle={handleFilterToggle}
        />
      )}

      <div className="relative flex-1">
        <ScrollableTableWrapper
          height="calc(100vh - 280px)"
          className="min-h-full"
        >
          <Table striped hoverable className="drop-shadow-none">
            <Table.Head>
              {columns
                .filter((col) => col.checked)
                .map((column, colIndex) => {
                  const orderingField = COLUMN_ORDERING_MAP[column.id];
                  const isSortable = !!orderingField;
                  const isAsc = ordering === orderingField;
                  const isDesc = ordering === `-${orderingField}`;
                  const isActive = isAsc || isDesc;

                  const handleSortClick = () => {
                    if (!isSortable || !onOrderingChange) return;
                    if (!isActive) {
                      onOrderingChange(orderingField); // First click: asc
                    } else if (isAsc) {
                      onOrderingChange(`-${orderingField}`); // Second click: desc
                    } else {
                      onOrderingChange(""); // Third click: clear
                    }
                  };

                  const sortIndicator = isActive ? (
                    isAsc ? (
                      <HiChevronUp className="ml-1 inline h-4 w-4" />
                    ) : (
                      <HiChevronDown className="ml-1 inline h-4 w-4" />
                    )
                  ) : null;

                  return (
                    <Table.HeadCell
                      key={column.id}
                      className={[
                        colIndex === 0
                          ? "sticky left-0 z-20 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] backdrop-blur-md"
                          : "",
                        isSortable
                          ? "cursor-pointer select-none hover:bg-gray-100"
                          : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      onClick={handleSortClick}
                    >
                      <span className="flex items-center">
                        {COLUMN_HEADER_TOOLTIP[column.id] ? (
                          <Tooltip
                            placement="bottom"
                            content={
                              <TooltipContent
                                title={COLUMN_HEADER_TOOLTIP[column.id]?.title}
                                size="lg"
                                text={COLUMN_HEADER_TOOLTIP[column.id]?.text()}
                              />
                            }
                            // eslint-disable-next-line react/style-prop-object
                            style="light"
                          >
                            {column.label}
                          </Tooltip>
                        ) : (
                          column.label
                        )}
                        {sortIndicator}
                      </span>
                    </Table.HeadCell>
                  );
                })}
            </Table.Head>
            <Table.Body className="divide-y">
              {isLoading || isSearching
                ? Array.from({ length: 8 }).map((_, index) => (
                    <SkeletonRow
                      key={`skeleton-${index}`}
                      numOfCells={selectedColumnIds.length - 1}
                    />
                  ))
                : customerList.map((customer) => (
                    <Table.Row
                      key={customer.id}
                      onClick={() => navigate(`/customers/${customer.id}`)}
                      className="animate-slide-up cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                    >
                      {columns
                        .filter((col) => col.checked)
                        .map((col) =>
                          renderCell(col.id, customer, formatDaysTillRenewal),
                        )}
                    </Table.Row>
                  ))}
            </Table.Body>
          </Table>
        </ScrollableTableWrapper>
      </div>

      <div className="sticky bottom-0 z-10 flex items-center justify-between border-t bg-white px-5 py-4 dark:bg-gray-900">
        <PaginationInfo page={page} pageSize={pageSize} total={total} />
        {totalPages > 0 && (
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={fetchNextPage}
            showIcons={true}
          />
        )}
      </div>
    </div>
  );
};
